import React, { useState, useContext } from "react";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";

const CreateStateForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    state_name: "",
    state_code: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiBase = getApiBaseURL();
  const { user } = useContext(AuthContext);
  const isPrimary = user?.is_primary;

  // HIPAA: NO localStorage usage
  // User info comes from secure context

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value.toUpperCase() }));
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.state_name.trim()) {
      setError("State name is required");
      return;
    }

    if (!formData.state_code.trim()) {
      setError("State code is required");
      return;
    }

    if (!/^[A-Z]{2}$/.test(formData.state_code)) {
      setError("State code must be exactly 2 letters");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // HIPAA Compliant: Using secureAxios with httpOnly cookies
      await secureAxios.post(`${apiBase}/api/states`, {
        state_name: formData.state_name.trim(),
        state_code: formData.state_code.trim(),
      });

      alert("✅ State created successfully!");
      setFormData({ state_name: "", state_code: "" });

      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("❌ Error creating state", err);
      setError(err.response?.data?.error || "Failed to create state");
    } finally {
      setLoading(false);
    }
  };

  // Check authorization
  if (!isPrimary) {
    return (
      <div className="alert alert-warning" role="alert">
        Only primary tenant users can create states.
      </div>
    );
  }

  return (
    <div>
      <h4>Create New State</h4>

      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="row g-3">
        <div className="col-md-8">
          <label htmlFor="state-name" className="form-label">
            State Name <span className="text-danger">*</span>
          </label>
          <input
            id="state-name"
            className="form-control"
            name="state_name"
            value={formData.state_name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, state_name: e.target.value }))
            }
            required
            maxLength={100}
            placeholder="e.g., California, New York"
            disabled={loading}
          />
        </div>

        <div className="col-md-4">
          <label htmlFor="state-code" className="form-label">
            State Code <span className="text-danger">*</span>
          </label>
          <input
            id="state-code"
            className="form-control text-uppercase"
            name="state_code"
            value={formData.state_code}
            onChange={handleChange}
            required
            maxLength={2}
            placeholder="e.g., CA, NY"
            disabled={loading}
          />
          <div className="form-text">2-letter abbreviation</div>
        </div>

        <div className="col-12">
          <button
            type="submit"
            className="btn btn-success"
            disabled={
              loading ||
              !formData.state_name.trim() ||
              !formData.state_code.trim()
            }
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Creating...
              </>
            ) : (
              "Create State"
            )}
          </button>

          <button
            type="button"
            className="btn btn-secondary ms-2"
            onClick={() => {
              setFormData({ state_name: "", state_code: "" });
              setError(null);
            }}
            disabled={loading}
          >
            Clear
          </button>
        </div>
      </form>

      {/* Global Impact Notice */}
      <div className="alert alert-warning mt-3" role="alert">
        <i className="bi bi-globe"></i> <strong>Global Impact:</strong>
        States are shared across all tenants. Changes affect the entire system.
      </div>
    </div>
  );
};

export default CreateStateForm;
